// Track 2 — Current subscription + plan catalog hook.
//
// Fetches GET /subscriptions/me + GET /plans + GET /subscriptions/me/usage
// once on mount. Caches the result in module scope so consumers don't
// double-fetch when multiple pages mount the hook at once. Listens for
// an in-process refresh event so checkout success flows can ask the
// hook to refetch without a full page reload.
//
// Wave 16 — adds `usage` (live-session metering) with a piggyback
// setter (`updateUsageFromHeartbeat`) so AIClientVision can push the
// server-provided snapshot straight into the cache each heartbeat
// without a separate GET.

import { useEffect, useState, useCallback } from 'react';
import { readCurrentUser } from './useCurrentUser';

export interface PlanRow {
  id: string;
  name: string;
  priceCents: number;
  currency: string;
  interval: string;
  features: string[];
  isActive: boolean;
}

export interface SubscriptionRow {
  id: string;
  userId: number;
  planId: string;
  status: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  createdAt: string;
  updatedAt: string;
  plan?: PlanRow;
}

export interface UsageSnapshot {
  planId: string;
  isFree: boolean;
  weeklyUsedSec: number;
  weeklyLimitSec: number | null;
  weeklyResetAt: string;
  dailyUsedSec: number;
  dailyIncludedSec: number | null;
  todayOverageSec: number;
  monthlyOverageSec: number;
  monthlyOverageCents: number;
  projectedMonthOverageCents: number;
  // Wave 20.44 — tier feature caps. Null maxPositions = unlimited
  // (Pro). Both fields are absent on responses from a backend older
  // than the paired usage.service.ts change; consumers should coerce
  // via `?? null` / `?? 8` so old bundles hitting an old server keep
  // rendering their previous UX.
  maxPositions?: number | null;
  weatherHorizonHours?: number;
  // Wave 20.68 — cents per minute past the daily allowance, served
  // rather than hardcoded so the price on the paywall cannot drift
  // from the price on the invoice. Absent on an older backend;
  // consumers coerce with `?? null` and omit the rate rather than
  // printing a number that might be wrong.
  overageCentsPerMin?: number;
}

/** Render a cents-per-minute rate the way a rider reads it. */
export function formatOverageRate(cents: number | null | undefined): string | null {
  if (cents == null || !Number.isFinite(cents)) return null;
  return `$${(cents / 100).toFixed(2)}/min`;
}

// Plans considered Pro-tier for entitlement. Mirrors PRO_PLAN_IDS in the
// backend usage.service.ts.
const PRO_PLAN_IDS = new Set<string>(['pro', 'pro_monthly', 'pro_yearly']);

export interface UseSubscriptionResult {
  plans: PlanRow[];
  subscription: SubscriptionRow | null;
  usage: UsageSnapshot | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  refreshUsage: () => Promise<void>;
  // Convenience flag for UI gates: true if the user is on a paid plan
  // OR has role='rapha' (Rapha members get full access regardless of plan).
  hasFullAccess: boolean;
}

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

// Module-scope cache so multiple mounts don't refetch.
let cachedPlans: PlanRow[] | null = null;
let cachedSub: SubscriptionRow | null = null;
let cachedUsage: UsageSnapshot | null = null;
let inflight: Promise<void> | null = null;
const subscribers = new Set<() => void>();

function notify() {
  subscribers.forEach((fn) => fn());
}

async function loadOnce(): Promise<void> {
  if (cachedPlans && cachedSub && cachedUsage) return;
  if (inflight) return inflight;
  inflight = (async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const [plansResp, subResp, usageResp] = await Promise.all([
      fetch(`${API}/plans`).then((r) => (r.ok ? r.json() : [])),
      token
        ? fetch(`${API}/subscriptions/me`, { headers }).then((r) =>
            r.ok ? r.json() : null,
          )
        : Promise.resolve(null),
      token
        ? fetch(`${API}/subscriptions/me/usage`, { headers }).then((r) =>
            r.ok ? r.json() : null,
          )
        : Promise.resolve(null),
    ]);
    cachedPlans = Array.isArray(plansResp) ? plansResp : [];
    cachedSub = subResp ?? null;
    cachedUsage = usageResp ?? null;
    inflight = null;
    notify();
  })();
  return inflight;
}

export async function refreshSubscription(): Promise<void> {
  cachedPlans = null;
  cachedSub = null;
  cachedUsage = null;
  inflight = null;
  await loadOnce();
}

async function refetchUsageOnly(): Promise<void> {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return;
  const r = await fetch(`${API}/subscriptions/me/usage`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (r.ok) {
    cachedUsage = await r.json();
    notify();
  }
}

/** Called by the live-session heartbeat handler with the fresh usage
 *  snapshot the server returned. Skips a network round-trip and keeps
 *  the HUD chip and dashboard card in sync in real time. */
export function updateUsageFromHeartbeat(snap: UsageSnapshot | null): void {
  if (!snap) return;
  cachedUsage = snap;
  notify();
}

export function useSubscription(): UseSubscriptionResult {
  const [, force] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(!cachedPlans || !cachedSub);

  useEffect(() => {
    const rerender = () => force((n) => n + 1);
    subscribers.add(rerender);
    loadOnce()
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
    return () => {
      subscribers.delete(rerender);
    };
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    await refreshSubscription().catch((e) => setError(String(e)));
    setLoading(false);
  }, []);

  const refreshUsage = useCallback(async () => {
    await refetchUsageOnly().catch((e) => setError(String(e)));
  }, []);

  const subscription = cachedSub;
  const plans = cachedPlans ?? [];
  const usage = cachedUsage;

  // Paid tiers (pro / pro_monthly / pro_yearly) unlock full features.
  // Rapha-role riders also get full access — their membership is the
  // unlock, not any particular subscription tier. (RaphaGuard handles
  // subdomain enforcement; this is the feature-breadth gate that
  // cascades through SessionDetail, PositionsAnalytics, etc.)
  const tier = subscription?.planId ?? 'free';
  const role = readCurrentUser().role;
  const hasFullAccess = PRO_PLAN_IDS.has(tier) || role === 'rapha';

  return {
    plans,
    subscription,
    usage,
    isLoading: loading,
    error,
    refresh,
    refreshUsage,
    hasFullAccess,
  };
}
